import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { CommissionConfigData } from "@/types/commissionConfig";
import { UpdateBtn } from "../Update";
import { DeleteBtn } from "@/components/Delete";
import {
  deleteCommissionConfigData,
  setStatusCommissionConfig,
  getShowCommissionConfig,
} from "@/Api/commissionConfig";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ComponentControlsPagination as Pagination } from "@/components/Pagination";
import { ShowBtn } from "@/components/Show";
import { Mask } from "@/utils/mask";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

type Props = {
  dataApi: ApiDataProps<CommissionConfigData>;
  pageActions: TypePageActions;
  updateFront: (data: any) => void;
};

export const TableData = ({ dataApi, pageActions, updateFront }: Props) => {
  const [loading, setLoading] = useState(false);
  const { hasPermission } = usePermissions();

  const handleToggleStatus = async (uuid: string) => {
    try {
      setLoading(true);
      const { data, ok } = await setStatusCommissionConfig(uuid);

      if (ok) {
        toast.success(data?.message || "Status alterado com sucesso!");
        updateFront((prev: ApiDataProps<CommissionConfigData>) => ({
          ...prev,
          data: prev.data.map((item) =>
            item.uuid === uuid ? { ...item, is_active: !item.is_active } : item
          ),
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderDetails = (data: CommissionConfigData) => (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium mb-3">Faixas de Comissão</h4>
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-zinc-100 dark:bg-zinc-800">
              <tr>
                <th className="px-4 py-2 text-left">Faixa</th>
                <th className="px-4 py-2 text-left">Valor Inicial</th>
                <th className="px-4 py-2 text-left">Valor Final</th>
                <th className="px-4 py-2 text-left">Meta Não Batida</th>
                <th className="px-4 py-2 text-left">Meta Batida</th>
                <th className="px-4 py-2 text-left">Super Meta</th>
              </tr>
            </thead>
            <tbody>
              {data.commission_ranges.map((range, index) => {
                const notHitValue = range.commission_range_values.find(
                  (v) => v.goal_result === "not_hit"
                );
                const hitValue = range.commission_range_values.find(
                  (v) => v.goal_result === "hit"
                );
                const superHitValue = range.commission_range_values.find(
                  (v) => v.goal_result === "super_hit"
                );

                return (
                  <tr key={range.id} className="border-t">
                    <td className="px-4 py-2">{index + 1}</td>
                    <td className="px-4 py-2">
                      R${" "}
                      {Number(range.initial_value).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-4 py-2">
                      R${" "}
                      {Number(range.final_value).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-4 py-2">
                      {(Number(notHitValue?.percentage) * 100).toFixed(2)}%
                    </td>
                    <td className="px-4 py-2">
                      {(Number(hitValue?.percentage) * 100).toFixed(2)}%
                    </td>
                    <td className="px-4 py-2">
                      {(Number(superHitValue?.percentage) * 100).toFixed(2)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {data.sellers?.length > 0 && (
        <div>
          <h4 className="font-medium mb-3">Vendedores Vinculados</h4>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full min-w-[400px]">
              <thead className="bg-zinc-100 dark:bg-zinc-800">
                <tr>
                  <th className="px-4 py-2 text-left">Nome</th>
                  <th className="px-4 py-2 text-left">Meta</th>
                </tr>
              </thead>
              <tbody>
                {data.sellers.map((seller) => (
                  <tr key={seller.id} className="border-t">
                    <td className="px-4 py-2">{seller?.user?.name}</td>
                    <td className="px-4 py-2">
                      {Mask.formatCurrency(seller?.pivot?.target_value || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Período</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Faixas</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.name}</TableCell>
              <TableCell>
                {format(new Date(item.start_date), "dd/MM/yyyy", {
                  locale: ptBR,
                })}{" "}
                -{" "}
                {format(new Date(item.end_date), "dd/MM/yyyy", {
                  locale: ptBR,
                })}
              </TableCell>
              <TableCell>{item.commission_type.name}</TableCell>
              <TableCell>{item.commission_ranges.length} faixas</TableCell>
              <TableCell className="text-center">
                <Switch
                  checked={item.is_active}
                  disabled={loading}
                  onCheckedChange={() => handleToggleStatus(item.uuid)}
                />
              </TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center">
                  <If
                    condition={hasPermission("show-commission-range-details")}
                  >
                    <ShowBtn
                      params={{
                        func: getShowCommissionConfig,
                        funcParams: item.uuid,
                      }}
                      className="sm:max-w-[90vw]"
                      render={renderDetails}
                      labels={{
                        title: `Detalhes - ${item.name}`,
                        description: "Informações detalhadas da configuração",
                      }}
                    />
                  </If>

                  <If condition={hasPermission("edit-commission-range")}>
                    <UpdateBtn uuid={item.uuid} updateFront={updateFront} />
                  </If>
                  <If condition={hasPermission("delete-commission-range")}>
                    <DeleteBtn
                      params={{
                        func: deleteCommissionConfigData,
                        funcParams: item.uuid,
                      }}
                      updateFront={() =>
                        updateFront((prev) => ({
                          ...prev,
                          data: prev.data.filter((i) => i.uuid !== item.uuid),
                        }))
                      }
                      referenceLabel={`a configuração "${item.name}"`}
                    />
                  </If>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {dataApi?.last_page > 1 && (
        <Pagination dataApi={dataApi} pageActions={pageActions} />
      )}
    </>
  );
};
